import * as React from 'react';
import PropTypes from 'prop-types';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import {
  Tooltip,
  Checkbox,
  TextField,
  Button,
  Box,
  Chip,
  InputAdornment,
  IconButton,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const HEADER_BG = '#E8951A';
const HEADER_TEXT = '#1A1205';
const SELECTED_BG = 'rgba(249, 170, 42, 0.14)';
const CHECKED_BG = 'rgba(48, 79, 255, 0.07)';
const HOVER_BG = 'rgba(15, 23, 42, 0.03)';
const BORDER = 'rgba(15, 23, 42, 0.08)';
const DIVIDER = 'rgba(15, 23, 42, 0.06)';

const headerCellSx = {
  background: `${HEADER_BG} !important`,
  color: HEADER_TEXT,
  textAlign: 'center',
  fontSize: '0.9rem',
  fontWeight: 800,
  letterSpacing: '0.01em',
  py: 1.35,
  px: 1.15,
  borderBottom: 'none',
  borderInlineEnd: '1px solid rgba(26, 18, 5, 0.18)',
  whiteSpace: 'nowrap',
  lineHeight: 1.25,
  '&:last-of-type': {
    borderInlineEnd: 'none',
  },
};

const bodyCellSx = {
  fontSize: '0.8125rem',
  fontWeight: 500,
  color: '#0F172A',
  py: 1.05,
  px: 1,
  borderBottom: `1px solid ${DIVIDER}`,
  borderInlineEnd: `1px solid ${DIVIDER}`,
  '&:last-of-type': {
    borderInlineEnd: 'none',
  },
};

export default function ClickAbleDenseTable({
  headers,
  content,
  styleFlag = false,
  width = '70%',
  height = '100px',
  numericrows = true,
  reverse = false,
  align = 'right',
  setSelectedRow,
  selectedRowIndex,
  openEdit,
  enableFilter = false,
  enableMultiSelect = false,
  onDeleteSelected,
  deleteButtonLabel,
}) {
  const { t } = useTranslation();
  const [selectedHeaderIndex, setSelectedHeaderIndex] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [selectedRows, setSelectedRows] = useState([]);

  const processedHeaders = reverse ? [...headers].reverse() : headers;

  // Keep track of each row's original index so filtering/selection stay correct.
  const filteredRows = useMemo(() => {
    const indexed = content.map((row, originalIndex) => ({ row, originalIndex }));
    if (!enableFilter || !filterText.trim()) {
      return indexed;
    }
    const query = filterText.trim().toLowerCase();
    return indexed.filter(({ row }) =>
      (Array.isArray(row) ? row : []).some((cell) => {
        if (cell == null) return false;
        if (typeof cell === 'string' || typeof cell === 'number') {
          return String(cell).toLowerCase().includes(query);
        }
        // Support Chip / React-node cells (e.g. Finsite linked badge).
        if (React.isValidElement(cell) && cell.props?.label) {
          return String(cell.props.label).toLowerCase().includes(query);
        }
        return false;
      })
    );
  }, [content, filterText, enableFilter]);

  const visibleIndices = filteredRows.map((item) => item.originalIndex);
  const allVisibleSelected =
    visibleIndices.length > 0 && visibleIndices.every((i) => selectedRows.includes(i));
  const someVisibleSelected =
    visibleIndices.some((i) => selectedRows.includes(i)) && !allVisibleSelected;

  const handleRowClick = (originalIndex) => {
    setSelectedRow(originalIndex);
  };

  const handleHeaderClick = (headerIndex) => {
    setSelectedHeaderIndex(headerIndex);
  };

  const handleRightClick = (event, originalIndex) => {
    event.preventDefault();
    openEdit(originalIndex);
  };

  const handleToggleRow = (event, originalIndex) => {
    event.stopPropagation();
    setSelectedRows((prev) =>
      prev.includes(originalIndex)
        ? prev.filter((i) => i !== originalIndex)
        : [...prev, originalIndex]
    );
  };

  const handleToggleSelectAll = (event) => {
    event.stopPropagation();
    if (allVisibleSelected) {
      setSelectedRows((prev) => prev.filter((i) => !visibleIndices.includes(i)));
    } else {
      setSelectedRows((prev) => Array.from(new Set([...prev, ...visibleIndices])));
    }
  };

  const handleDeleteSelected = async () => {
    if (!onDeleteSelected || selectedRows.length === 0) return;
    await onDeleteSelected([...selectedRows]);
    setSelectedRows([]);
  };

  const showToolbar = enableFilter || enableMultiSelect;
  const colCount = processedHeaders.length + (enableMultiSelect ? 1 : 0);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width, minWidth: 0 }}>
      {showToolbar && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            mb: 1,
          }}
        >
          {enableFilter && (
            <TextField
              size="small"
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              placeholder={t('Search')}
              sx={{
                flex: '1 1 220px',
                bgcolor: '#fff',
                borderRadius: '10px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  fontSize: '0.8125rem',
                  '& fieldset': { borderColor: BORDER },
                  '&:hover fieldset': { borderColor: 'rgba(15, 23, 42, 0.18)' },
                  '&.Mui-focused fieldset': { borderColor: '#304FFF', borderWidth: '1.5px' },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'rgba(15, 23, 42, 0.4)' }} />
                  </InputAdornment>
                ),
                endAdornment: filterText ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setFilterText('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
          {enableMultiSelect && (
            <>
              {selectedRows.length > 0 && (
                <Chip
                  label={`${selectedRows.length} ${t('Selected')}`}
                  size="small"
                  onDelete={() => setSelectedRows([])}
                  sx={{
                    borderColor: 'rgba(48, 79, 255, 0.35)',
                    color: '#304FFF',
                    bgcolor: 'rgba(48, 79, 255, 0.06)',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                  variant="outlined"
                />
              )}
              <Button
                variant="contained"
                color="error"
                size="small"
                startIcon={<DeleteOutlineIcon />}
                disabled={selectedRows.length === 0}
                onClick={handleDeleteSelected}
                sx={{
                  height: 36,
                  textTransform: 'none',
                  borderRadius: '10px',
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': { boxShadow: 'none' },
                }}
              >
                {deleteButtonLabel || t('DeleteSelected')}
              </Button>
            </>
          )}
        </Box>
      )}
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          width: '100%',
          height,
          overflowX: 'auto',
          overflowY: 'auto',
          borderRadius: '12px',
          border: `1px solid ${BORDER}`,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04), 0 4px 12px rgba(15, 23, 42, 0.04)',
          bgcolor: '#fff',
        }}
      >
        <Table size="small" aria-label="a dense table" stickyHeader>
          <TableHead>
            <TableRow>
              {enableMultiSelect && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    ...headerCellSx,
                    width: 44,
                    px: 0.5,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={allVisibleSelected}
                    indeterminate={someVisibleSelected}
                    onClick={handleToggleSelectAll}
                    sx={{
                      color: 'rgba(26, 18, 5, 0.55)',
                      '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#1A1205' },
                    }}
                  />
                </TableCell>
              )}
              {processedHeaders.map((header, headerIndex) => (
                <TableCell
                  key={header}
                  align={align}
                  onClick={() => handleHeaderClick(headerIndex)}
                  sx={{
                    ...headerCellSx,
                    cursor: 'pointer',
                    opacity: selectedHeaderIndex === headerIndex ? 0.92 : 1,
                  }}
                >
                  {header}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  align="center"
                  sx={{ color: 'rgba(15, 23, 42, 0.4)', py: 2.5, fontSize: '0.8125rem' }}
                >
                  {t('NoData')}
                </TableCell>
              </TableRow>
            ) : (
              filteredRows.map(({ row, originalIndex }) => {
                const displayRow = reverse ? [...row].reverse() : row;
                const isChecked = selectedRows.includes(originalIndex);
                const isSelected = selectedRowIndex === originalIndex;
                return (
                  <Tooltip key={originalIndex} title={t('RightClickToEdit')} placement="top" arrow>
                    <TableRow
                      onClick={() => handleRowClick(originalIndex)}
                      onContextMenu={(event) => handleRightClick(event, originalIndex)}
                      selected={isSelected}
                      sx={{
                        bgcolor: isSelected
                          ? SELECTED_BG
                          : isChecked
                            ? CHECKED_BG
                            : 'transparent',
                        cursor: 'pointer',
                        transition: 'background-color 0.12s ease',
                        '&:hover': {
                          bgcolor: isSelected
                            ? 'rgba(249, 170, 42, 0.2)'
                            : isChecked
                              ? 'rgba(48, 79, 255, 0.1)'
                              : HOVER_BG,
                        },
                        '&:last-of-type td': {
                          borderBottom: 'none',
                        },
                        borderInlineStart: isSelected ? '3px solid #F9AA2A' : '3px solid transparent',
                        boxSizing: 'border-box',
                      }}
                    >
                      {enableMultiSelect && (
                        <TableCell
                          padding="checkbox"
                          sx={{
                            ...bodyCellSx,
                            width: 44,
                            px: 0.5,
                          }}
                        >
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            onClick={(event) => handleToggleRow(event, originalIndex)}
                            sx={{
                              color: 'rgba(15, 23, 42, 0.3)',
                              '&.Mui-checked': { color: '#304FFF' },
                            }}
                          />
                        </TableCell>
                      )}
                      {displayRow.map((cell, cellIndex) => (
                        <TableCell
                          key={cellIndex}
                          align="center"
                          sx={{
                            ...bodyCellSx,
                            ...(styleFlag &&
                            cellIndex === 0 &&
                            String(cell).trim() !== '0' &&
                            String(cell).trim() !== '0.00'
                              ? { bgcolor: 'rgba(240, 103, 93, 0.18)', fontWeight: 700 }
                              : null),
                          }}
                        >
                          {cell}
                        </TableCell>
                      ))}
                    </TableRow>
                  </Tooltip>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

ClickAbleDenseTable.propTypes = {
  headers: PropTypes.arrayOf(PropTypes.string).isRequired,
  content: PropTypes.arrayOf(PropTypes.array).isRequired,
  styleFlag: PropTypes.bool,
  width: PropTypes.string,
  height: PropTypes.string,
  numericrows: PropTypes.bool,
  reverse: PropTypes.bool,
  setSelectedRow: PropTypes.func.isRequired,
  selectedRowIndex: PropTypes.number,
  openEdit: PropTypes.func.isRequired,
  enableFilter: PropTypes.bool,
  enableMultiSelect: PropTypes.bool,
  onDeleteSelected: PropTypes.func,
  deleteButtonLabel: PropTypes.string,
};
